import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

export type SceneRefs = {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  canvas: HTMLCanvasElement;
};

export function createScene(canvas: HTMLCanvasElement): SceneRefs {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();

  // Soft warm ambient + cool fill
  scene.add(new THREE.AmbientLight(0xfff4ea, 0.55));
  const hemi = new THREE.HemisphereLight(0xfff4ea, 0xe5f2f8, 0.45);
  scene.add(hemi);

  // Key light
  const key = new THREE.DirectionalLight(0xfff5ec, 1.05);
  key.position.set(2.4, 3.0, 2.0);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.camera.near = 0.1;
  key.shadow.camera.far = 12;
  key.shadow.camera.left = -3;
  key.shadow.camera.right = 3;
  key.shadow.camera.top = 3;
  key.shadow.camera.bottom = -3;
  key.shadow.bias = -0.0005;
  key.shadow.radius = 6;
  scene.add(key);

  // Cool fill from opposite side
  const fill = new THREE.DirectionalLight(0xe5f2f8, 0.30);
  fill.position.set(-2.5, 1.6, -1.5);
  scene.add(fill);

  // Subtle ground shadow plane (transparent shadow material)
  const ground = new THREE.Mesh(
    new THREE.CircleGeometry(3.4, 48),
    new THREE.ShadowMaterial({ opacity: 0.18, transparent: true }),
  );
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = 0;
  ground.receiveShadow = true;
  scene.add(ground);

  // Camera
  const camera = new THREE.PerspectiveCamera(38, 1, 0.05, 50);
  camera.position.set(2.4, 1.7, 2.4);

  // Controls
  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.07;
  controls.minDistance = 1.2;
  controls.maxDistance = 6.5;
  controls.maxPolarAngle = Math.PI / 2.05;
  controls.target.set(0, 0.40, 0);

  // Resize
  function resize() {
    const r = canvas.getBoundingClientRect();
    renderer.setSize(r.width, r.height, false);
    camera.aspect = r.width / Math.max(1, r.height);
    camera.updateProjectionMatrix();
  }
  resize();
  const ro = new ResizeObserver(resize);
  ro.observe(canvas.parentElement ?? canvas);

  return { renderer, scene, camera, controls, canvas };
}

export function startRenderLoop(refs: SceneRefs, frameHook?: (dt: number) => void) {
  const clock = new THREE.Clock();
  function loop() {
    const dt = clock.getDelta();
    refs.controls.update();
    frameHook?.(dt);
    refs.renderer.render(refs.scene, refs.camera);
    requestAnimationFrame(loop);
  }
  loop();
}
